'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

import Image from 'next/image';
import Link from 'next/link';

interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  image: string;
  alt: string;
}

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const featuredProjects: Project[] = [
    {
      id: 1,
      title: "Designs That Feel Like Home",
      category: "Residential Interior",
      location: "Bangalore",
      image: "/hero1.jpg",
      alt: "Transformed a traditional villa into a sleek, modern home with improved flow and light."
    },
    {
      id: 2,
      title: "Heritage Home Revival",
      category: "Renovation & Restoration",
      location: "Indiranagar",
      image: "/hero2.jpg",
      alt: "Transforming spaces into timeless experiences through meticulous design and uncompromising attention to detail."
    },
    {
      id: 3,
      title: "Luxury Apartment Transformation",
      category: "Turnkey Project",
      location: "Koramangala",
      image: "/hero3.jpg",
      alt: "Elegant apartment interior with marble flooring, contemporary furniture, and artistic wall decor"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredProjects.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % featuredProjects.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + featuredProjects.length) % featuredProjects.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-black pt-20">
      {/* Background Images */}
      <div className="absolute inset-0 top-20">
        {featuredProjects.map((project, index) => (
          <div
            key={project.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <Image
              src={project.image}
              alt={project.alt}
              fill
              className="object-cover scale-105"
              priority={index === 0}
              quality={90}
            />
            {/* Lighter overlay to showcase images */}

         

            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center py-16 md:py-24 lg:py-32">
        <div className="w-full px-4 md:px-8 lg:px-20 max-w-[1400px] mx-auto">
          <div className="max-w-5xl space-y-6 md:space-y-8 animate-fade-in">
            
         

            {/* Large Typography */}
            <div className="space-y-3 md:space-y-4 mt-12 md:mt-16">
              <h1 className="font-headline text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-normal leading-tight md:leading-[0.95] text-white tracking-tight">
                {featuredProjects[currentSlide].title.split(' ').slice(0, 2).join(' ')}
                <span className="block font-normal mt-1 md:mt-2">
                  {featuredProjects[currentSlide].title.split(' ').slice(2).join(' ')}
                </span>
              </h1>

             

            </div>

            {/* Clean Description */}
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed max-w-2xl font-normal">
              {featuredProjects[currentSlide].alt}
            </p>

            {/* Refined CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 md:gap-5 pt-2 md:pt-4">
              <a
                href="#consultation"
                onClick={(e) => handleSmoothScroll(e, '#consultation')}
                className="group relative px-8 md:px-10 py-4 md:py-5 bg-white text-black font-medium rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-white/20 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-zinc-100 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative flex items-center justify-center gap-2 md:gap-3 text-sm md:text-base">
                  <span>Begin Your Project</span>
                  <Icon name="ArrowRightIcon" size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
              
              <Link
                href="/our-projects"
                className="group px-8 md:px-10 py-4 md:py-5 bg-transparent border border-white/20 hover:border-white/40 text-white font-medium rounded-full transition-all duration-500 hover:bg-white/5 text-center"
              >
                <span className="flex items-center justify-center gap-2 md:gap-3 text-sm md:text-base">
                  <span>View Portfolio</span>
                </span>
              </Link>
            </div>

          </div>
        </div>
      </div>

      {/* Elegant Navigation Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300 group active:scale-95 transform-gpu"
        aria-label="Previous project"
      >
        <Icon name="ChevronLeftIcon" size={18} className="text-white/80 group-hover:text-white transition-colors md:w-5 md:h-5" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300 group active:scale-95 transform-gpu"
        aria-label="Next project"
      >
        <Icon name="ChevronRightIcon" size={18} className="text-white/80 group-hover:text-white transition-colors md:w-5 md:h-5" />
      </button>

      {/* Clean Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 pb-6 md:pb-8 px-4 md:px-8 lg:px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between pt-4 md:pt-6">
            
            {/* Project Counter */}
            <div className="text-white/60 text-xs md:text-sm font-normal tracking-wider">
              <span className="text-white text-base md:text-lg">{String(currentSlide + 1).padStart(2, '0')}</span>
              <span className="mx-1 md:mx-2">/</span>
              <span>{String(featuredProjects.length).padStart(2, '0')}</span>
            </div>

            {/* Minimal Dots */}
            <div className="flex gap-1.5 md:gap-2">
              {featuredProjects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className="group"
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <div className={`h-0.5 rounded-full transition-all duration-500 ${
                    index === currentSlide 
                      ? 'w-8 md:w-12 bg-white' 
                      : 'w-4 md:w-6 bg-white/30 group-hover:bg-white/50'
                  }`} />
                </button>
              ))}
            </div>

            {/* Scroll Indicator */}
            <div className="hidden lg:flex items-center gap-3 text-white/40">
              <span className="text-xs font-normal tracking-widest uppercase">Scroll</span>
              <div className="w-px h-10 bg-white/20 relative overflow-hidden">
                <div className="absolute w-full h-6 bg-gradient-to-b from-transparent via-white/60 to-transparent animate-scroll" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(30px);
          }
          to { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        @keyframes scroll {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
        .animate-fade-in {
          animation: fade-in 1.2s ease-out forwards;
        }
        .animate-scroll {
          animation: scroll 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;